import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFreePlay } from '../../context/FreePlayContext';
import { useAuth } from '../../context/AuthContext';

// Mansion "Home Button" mirrored from the tanks main screen: a solid mansion
// silhouette with cut-out windows/door. It's inline SVG (no asset file).
const IconMansion = () => (
  <svg viewBox="0 0 36 36" className="app-header__mansion" aria-hidden="true">
    {/* Mansion silhouette: center peaked roof + two symmetrical wings */}
    <path d="M18,2 L24,8 L24,5 L26,5 L26,10 L28,12 L28,26 L8,26 L8,12 L10,10 L10,5 L12,5 L12,8 L18,2 Z M4,16 L8,12 L8,26 L4,26 Z M28,12 L32,16 L32,26 L28,26 Z" />
    {/* Windows - 2nd floor */}
    <rect className="app-header__mansion-window" x="13" y="10" width="3" height="3" />
    <rect className="app-header__mansion-window" x="20" y="10" width="3" height="3" />
    {/* Windows - 1st floor */}
    <rect className="app-header__mansion-window" x="13" y="16" width="3" height="3" />
    <rect className="app-header__mansion-window" x="20" y="16" width="3" height="3" />
    {/* Door */}
    <rect className="app-header__mansion-window" x="16" y="20" width="4" height="6" />
    {/* Wing windows */}
    <rect className="app-header__mansion-window" x="5" y="18" width="2" height="3" />
    <rect className="app-header__mansion-window" x="29" y="18" width="2" height="3" />
    {/* Driveway/ground base */}
    <rect x="10" y="26" width="16" height="2" />
    {/* Columns/pillars at entrance */}
    <rect x="14" y="20" width="1" height="6" />
    <rect x="21" y="20" width="1" height="6" />
  </svg>
);

// Bold, solid menu icons: filled silhouettes (Material-style), chunky and
// dark so they read instantly — not thin outlines, not pixel art.
const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  style: { display: 'block' },
  'aria-hidden': true
};

const IconDashboard = () => (
  <svg {...iconProps}>
    <rect x="4" y="10" width="4" height="10" rx="1" />
    <rect x="10" y="4" width="4" height="16" rx="1" />
    <rect x="16" y="13" width="4" height="7" rx="1" />
  </svg>
);

const IconPlay = () => (
  <svg {...iconProps}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const IconCoin = () => (
  // Credit card for the buy item
  <svg {...iconProps}>
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </svg>
);

const IconBook = () => (
  <svg {...iconProps}>
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const IconUser = () => (
  <svg {...iconProps}>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const IconBulb = () => (
  <svg {...iconProps}>
    <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" />
  </svg>
);

const IconTrophy = () => (
  <svg {...iconProps}>
    <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
  </svg>
);

const Header = ({ darkMode, onToggleDarkMode }) => {
  const { openPayment } = useFreePlay();
  const { isAuthenticated, signout, openAuth } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleBuyClick = useCallback(() => {
    if (!isAuthenticated) {
      openAuth('purchase');
    } else {
      openPayment('header');
    }
    setMenuOpen(false);
  }, [isAuthenticated, openAuth, openPayment]);

  const handleAuthClick = useCallback(() => {
    if (isAuthenticated) {
      signout();
    } else {
      openAuth('header');
    }
    setMenuOpen(false);
  }, [isAuthenticated, signout, openAuth]);

  const handleDashboardClick = useCallback(() => {
    navigate('/dashboard');
    setMenuOpen(false);
  }, [navigate]);

  const handleGameClick = useCallback(() => {
    navigate('/game');
    setMenuOpen(false);
  }, [navigate]);

  const handleInstructionsClick = useCallback(() => {
    navigate('/instructions');
    setMenuOpen(false);
  }, [navigate]);

  const handleLeaderboardClick = useCallback(() => {
    navigate('/leaderboard');
    setMenuOpen(false);
  }, [navigate]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
  }, []);

  return (
    <header className="app-header">
      <div className="app-header__menu-container">
        <button
          type="button"
          className="app-header__home-button"
          onClick={toggleMenu}
          aria-label="Menu"
          aria-expanded={menuOpen}
        >
          <IconMansion />
        </button>

        {menuOpen && (
          <>
            <div className="app-header__menu-overlay" onClick={() => setMenuOpen(false)} />
            <div className="app-header__menu-dropdown">
              <button
                type="button"
                className="app-header__menu-item"
                onClick={handleDashboardClick}
              >
                <span className="app-header__menu-icon"><IconDashboard /></span>
                Dashboard
              </button>
              <button
                type="button"
                className="app-header__menu-item"
                onClick={handleGameClick}
              >
                <span className="app-header__menu-icon"><IconPlay /></span>
                Spin Now
              </button>
              <button
                type="button"
                className="app-header__menu-item"
                onClick={handleInstructionsClick}
              >
                <span className="app-header__menu-icon"><IconBook /></span>
                Instructions
              </button>
              <button
                type="button"
                className="app-header__menu-item"
                onClick={handleLeaderboardClick}
              >
                <span className="app-header__menu-icon"><IconTrophy /></span>
                Leaderboard
              </button>
              <button
                type="button"
                className="app-header__menu-item"
                onClick={onToggleDarkMode}
              >
                <span className="app-header__menu-icon"><IconBulb /></span>
                Dark Mode
                <span
                  className={
                    darkMode
                      ? 'app-header__menu-toggle app-header__menu-toggle--on'
                      : 'app-header__menu-toggle'
                  }
                />
              </button>
              <div className="app-header__menu-divider" />
              <button
                type="button"
                className="app-header__menu-item app-header__menu-item--highlight"
                onClick={handleBuyClick}
              >
                <span className="app-header__menu-icon"><IconCoin /></span>
                Buy Spins
              </button>
              <button
                type="button"
                className="app-header__menu-item"
                onClick={() => { window.location.href = 'https://www.satoshiadvance.com'; }}
              >
                SatoshiAdvance.com
              </button>
              <div className="app-header__menu-divider" />
              <button
                type="button"
                className="app-header__menu-item"
                onClick={handleAuthClick}
              >
                <span className="app-header__menu-icon"><IconUser /></span>
                {isAuthenticated ? 'Log Out' : 'Log In / Sign Up'}
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;

